import { showSuccess } from '@calendly/alert';
import { usePageTitle } from '@calendly/hooks';
import { I18n } from '@calendly/i18n';
import { Button } from '@calendly/ui/components/button';
import { Loader } from '@calendly/ui/components/loader';
import { Sheet } from '@calendly/ui/components/sheet';
import { BodyText, HeadlineText } from '@calendly/ui/components/typography';
import classNames from 'classnames';
import { useEffect, useState, forwardRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import { useFetchSeatRequestQuery, useSubmitSeatRequestDeclineMutation } from './api';
import { TRACKING_EVENT_NAMES, useTrack, useTrackOnce } from '../../../hooks/analytics_events';
import { ownerNameSelector } from '../../../selectors/current_organization';
const wrapperCss = "w1wyu2pg";
const Wrapper = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(wrapperCss, props.className)} />;
});
const modalCss = "mbqroai";
const Modal = forwardRef((props, ref) => <Sheet ref={ref} {...props} className={classNames(modalCss, props.className)} />);
const disclaimerCss = "d16cahej";
const Disclaimer = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(disclaimerCss, props.className)} />;
});
const textFieldTitleCss = "t1e1k4wr";
const TextFieldTitle = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(textFieldTitleCss, props.className)} />;
});
const buttonsWrapperCss = "bnfiov";
const ButtonsWrapper = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(buttonsWrapperCss, props.className)} />;
});
const emailBodyCss = "et7s5nf";
const EmailBody = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'textarea';
  return <Component ref={ref} {...restProps} className={classNames(emailBodyCss, props.className)} />;
});
export const DeclineJoinRequest = () => {
  usePageTitle(I18n.t('publisher.admin.seat_requests.decline_join_request.title'));
  const history = useHistory();
  const dispatch = useDispatch();
  const ownerName = useSelector(ownerNameSelector);
  const defaultEmailBody = I18n.t('publisher.admin.seat_requests.decline_join_request.default_email', {
    ownerName
  }).replace(/\\n/g, '\n');
  const {
    uuid
  } = useParams();
  const [emailBody, setEmailBody] = useState(defaultEmailBody);
  const {
    isLoading,
    data,
    isError
  } = useFetchSeatRequestQuery(uuid);
  const [submitDecline, {
    isLoading: isDeclineLoading,
    data: declineResponse
  }] = useSubmitSeatRequestDeclineMutation();
  const track = useTrack();
  useTrackOnce(true, {
    name: TRACKING_EVENT_NAMES.MODAL_INTERACTION,
    properties: {
      action: 'opening',
      modal_name: 'decline_request_to_join',
      location: 'user_request_to_join_email',
      path: '/app/admin/decline_join_request'
    }
  });
  useEffect(() => {
    if (isError) history.push('/app/admin/dashboard'); // Unauthorized, push back to admin page
  }, [history, isError]);
  useEffect(() => {
    if (declineResponse?.success) {
      dispatch(showSuccess(I18n.t('publisher.admin.seat_requests.decline_join_request.success_toast', {
        name: data.organizationJoinRequest.requesterName
      })));
      history.push('/app/scheduling/meeting_types/user/me');
    }
  }, [data, declineResponse, dispatch, history]);
  const handleSendEmail = () => {
    track({
      name: TRACKING_EVENT_NAMES.MODAL_INTERACTION,
      properties: {
        action: 'send_email',
        modal_name: 'decline_request_to_join',
        location: 'user_request_to_join_email',
        path: '/app/admin/decline_join_request'
      }
    });
    submitDecline({
      uuid,
      emailBody
    });
  };
  if (isLoading) {
    return <Wrapper>
        <Modal>
          <Loader />
        </Modal>
      </Wrapper>;
  }
  const joinRequest = data.organizationJoinRequest;
  if (joinRequest.status === 'declined' || joinRequest.status === 'accepted') {
    return <Wrapper>
        <Modal>
          <HeadlineText size="small" level="2">
            {I18n.t('publisher.admin.seat_requests.decline_join_request.already_processed.header', {
            name: joinRequest.requesterName
          })}
          </HeadlineText>

          <Disclaimer>
            <BodyText size="large">
              {I18n.t('publisher.admin.seat_requests.decline_join_request.already_processed.body', {
              name: joinRequest.requesterName,
              status: joinRequest.status
            })}
            </BodyText>
          </Disclaimer>

          <Button onClick={() => history.push('/app/admin/dashboard')} wide>
            {I18n.t('publisher.action.cancel')}
          </Button>
        </Modal>
      </Wrapper>;
  }
  return <Wrapper>
      <Modal>
        <HeadlineText size="small" level="2">
          {I18n.t('publisher.admin.seat_requests.decline_join_request.header', {
          name: joinRequest.requesterName
        })}
        </HeadlineText>

        <Disclaimer>
          <BodyText size="large">
            {I18n.t('publisher.admin.seat_requests.decline_join_request.disclaimer', {
            email: joinRequest.requesterEmail
          })}
          </BodyText>
        </Disclaimer>

        <TextFieldTitle>
          <BodyText weight="bold" size="large">
            {I18n.t('publisher.admin.seat_requests.decline_join_request.text_field_title')}
          </BodyText>
        </TextFieldTitle>

        <EmailBody onChange={e => setEmailBody(e.target.value)} value={emailBody} />

        <ButtonsWrapper>
          <Button decoration="secondary-outline" onClick={() => {
          track({
            name: TRACKING_EVENT_NAMES.MODAL_INTERACTION,
            properties: {
              action: 'dismissed',
              modal_name: 'decline_request_to_join',
              location: 'user_request_to_join_email',
              path: '/app/admin/decline_join_request'
            }
          });
          history.push('/app/admin/dashboard');
        }} wide spacingRight={8} loading={isDeclineLoading}>
            {I18n.t('publisher.action.cancel')}
          </Button>
          <Button onClick={handleSendEmail} wide loading={isDeclineLoading}>
            {I18n.t('publisher.billing.contact_sales.send')}
          </Button>
        </ButtonsWrapper>
      </Modal>
    </Wrapper>;
};

require("./decline_join_request.wyw-in-js.css!=!../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./decline_join_request.js");